import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import "./TransactionList.css";
import useTransactions from "../../../hooks/useTransactions";
import TransactionItem from "./components/TransactionItem";
import PaginationControls from "./components/PaginationControls";
import TransactionsSummary from "./components/TransactionsSummary";
import SearchBar from "./components/SearchBar";
import NoTransactions from "./components/NoTransactions";

const TransactionList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);

  const { id } = useParams();
  const stationId = id;

  const { transactions, setTransactions, loading } = useTransactions(
    stationId,
    currentYear,
    currentMonth
  );

  const today = new Date();
  const isCurrentMonth =
    currentYear === today.getFullYear() &&
    currentMonth === today.getMonth() + 1;

  const goToNextMonth = () => {
    if (!isCurrentMonth && !loading) {
      if (currentMonth === 12) {
        setCurrentMonth(1);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const goToPreviousMonth = () => {
    if (!loading) {
      if (currentMonth === 1) {
        setCurrentMonth(12);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    }
  };

  const clearTransactions = () => {
    setTransactions([]);
  };

  const filteredTransactions = useMemo(() => {
    return transactions?.filter((transaction) =>
      transaction?.transactionId
        ?.toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  }, [transactions, searchTerm]);

  const totalCostExcludingFee = useMemo(() => {
    if (!filteredTransactions || filteredTransactions?.length === 0) {
      return "0.00";
    }

    return filteredTransactions
      .reduce((total, transaction) => {
        if (
          transaction?.chargedAmount !== "Free" &&
          transaction?.chargedAmount
        ) {
          const amount = parseFloat(
            transaction.chargedAmount.replace("€", "").replace(",", ".")
          );
          return Number((total + amount).toFixed(2));
        }
        return total;
      }, 0)
      .toFixed(2);
  }, [filteredTransactions]);

  const totalCostWithFee = useMemo(() => {
    if (!filteredTransactions || filteredTransactions?.length === 0) {
      return "0.00";
    }

    return filteredTransactions
      .reduce((total, transaction) => {
        if (
          transaction?.chargedAmountWithFee !== "Free" &&
          transaction?.chargedAmountWithFee
        ) {
          const amount = parseFloat(
            transaction.chargedAmountWithFee.replace("€", "").replace(",", ".")
          );
          return Number((total + amount).toFixed(2));
        }
        return total;
      }, 0)
      .toFixed(2);
  }, [filteredTransactions]);

  const totalPaidEnergy = useMemo(() => {
    return (
      filteredTransactions
        ?.filter((t) => t?.status !== "Free")
        .reduce((sum, t) => sum + (parseFloat(t?.energy) || 0), 0)
        .toFixed(2) ?? "0.00"
    );
  }, [filteredTransactions]);

  const totalFreeEnergy = useMemo(() => {
    return (
      filteredTransactions
        ?.filter((t) => t?.status === "Free")
        .reduce((sum, t) => sum + (parseFloat(t?.energy) || 0), 0)
        .toFixed(2) ?? "0.00"
    );
  }, [filteredTransactions]);

  return (
    <div className="layout-main">
      <div className="surface-section">
        <div className="grid">
          <div className="col-12">
            <Card className="shadow-2 border-round">
              <h2 className="text-2xl font-bold mb-4">Transaction List</h2>

              <div className="mb-4">
                <SearchBar
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  className="w-full"
                />
              </div>

              <div className="transaction-list">
                {loading ? (
                  <div
                    className="flex justify-content-center align-items-center"
                    style={{ minHeight: "150px" }}
                  >
                    <ProgressSpinner />
                  </div>
                ) : filteredTransactions?.length === 0 ? (
                  <NoTransactions
                    monthLabel={new Date(currentYear, currentMonth - 1).toLocaleString("default", { month: "long", year: "numeric" })}
                    isSearchEmpty={searchTerm.length > 0}
                  />
                ) : (
                  filteredTransactions?.map((transaction) => (
                    <TransactionItem
                      key={transaction?.id}
                      transaction={transaction}
                      className="mb-3"
                    />
                  ))
                )}
              </div>

              <div className="mt-4">
                <PaginationControls
                  currentYear={currentYear}
                  currentMonth={currentMonth}
                  goToPreviousMonth={goToPreviousMonth}
                  goToNextMonth={goToNextMonth}
                  isCurrentMonth={isCurrentMonth}
                  loading={loading}
                />
              </div>

              <div className="mt-4">
                <TransactionsSummary
                  totalCostExcludingFee={totalCostExcludingFee}
                  totalCostWithFee={totalCostWithFee}
                  totalTransactions={filteredTransactions?.length}
                  totalPaidEnergy={totalPaidEnergy}
                  totalFreeEnergy={totalFreeEnergy}
                  clearTransactions={clearTransactions}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionList;
